import {Button, message, Modal, Space, Table, Tag, Tree, Typography} from "antd";
import {ClockCircleTwoTone, InfoCircleTwoTone} from "@ant-design/icons"
import React, {useEffect, useState} from "react";
import Api from "../common/Api";
import StorageHelper from "../common/StorageHelper";
import ModalGroup from "./ModalGroup";
import { DateTime } from "luxon";

const {Text} = Typography;

const {Column, ColumnGroup} = Table;

export enum Type {
    CONFIRMED = 'CONFIRMED',
    UNCONFIRMED = 'UNCONFIRMED',
    FORCE_CHANGE_PASSWORD = 'FORCE_CHANGE_PASSWORD'
}

interface Props {
    type: Type;
    refresh: number;
}

interface DataType {
    key: React.Key;
    Username: string;
    AuthEvents: Array<any>;
    Attributes: [{ Name: string; Value: string; }];
    UserCreateDate: string;
    UserLastModifiedDate: string;
    Enabled: boolean;
    UserStatus: Type;
}

const getUserParams = {
    CONFIRMED: {filters: ['cognito:user_status'], values: ['CONFIRMED']},
    UNCONFIRMED: {filters: ['cognito:user_status'], values: ['UNCONFIRMED']},
    FORCE_CHANGE_PASSWORD: {filters: ['cognito:user_status'], values: ['FORCE_CHANGE_PASSWORD']},
}

const DeleteMessage = (props: { username: string }) => {
    return (
        <>
            <Text>Are you sure you want to delete </Text>
            <Text strong>{props.username}</Text><Text> user account permanently ?</Text><br/><br/>
            <Text strong>You can't undo this action, </Text>
            <Text>to confirm deletion, press</Text> <Text strong>Confirm</Text>
        </>
    )
}

const ApproveMessage = (props: { username: string }) => {
    return (
        <>
            <Text>Are you sure you want to approve </Text>
            <Text strong>{props.username}</Text><Text> user account ?</Text><br/><br/>
            <Text strong>You can't undo this action, </Text>
            <Text>to approve this user, press</Text> <Text strong>Confirm</Text>
        </>
    )
}

const GroupMessage = (props: { username: string, group: string }) => {
    return (
        <>
            <Text>Are you sure you want to remove </Text>
            <Text strong>{props.username}</Text><Text> from <b>{props.group}</b> group ?</Text><br/><br/>
            <Text>to approve this action, press</Text> <Text strong>Confirm</Text>
        </>
    )
}

const find = (collection: any[], value: string) => {
    const index = collection.findIndex((item) => item.Name === value)
    return collection[index]
}

const UserList = ({type, refresh}: Props) => {
    const [users, setUsers] = useState([]);
    const [isVisible, setIsVisible] = useState(false);
    const [user, setUser] = useState<undefined | string>()
    const [localRefresh, setLocalRefresh] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true)
            const {filters, values} = getUserParams[type]
            const {data: firstCall} = await Api.usersManagement.getUsers(filters, values)
            if (type === Type.UNCONFIRMED) {
                const {filters, values} = getUserParams["FORCE_CHANGE_PASSWORD"]
                const {data: secondCall} = await Api.usersManagement.getUsers(filters, values)
                firstCall.Users = [...secondCall.Users, ...firstCall.Users];
            }
            console.log(firstCall)
            setUsers(firstCall.Users)
            setLoading(false)
        })();
    }, [type, refresh, localRefresh])


    const confirmAction = (username: string, action: "approve" | "delete" | "group", group?: string) => {
        const base = {
            okText: 'Confirm',
            cancelText: 'Cancel',
        }
        const actions = {
            delete: {
                ...base,
                title: 'Delete user account',
                content: <DeleteMessage username={username}/>,
                onOk: async () => {
                    try {
                        await Api.usersManagement.deleteUser(username)
                        setLocalRefresh(localRefresh + 1);
                        message.success("User deleted!");
                    } catch (e) {
                        message.error("Error while deleting user");
                        throw (e)
                    }
                }
            },
            group: {
                ...base,
                title: 'Remove user from group',
                content: <GroupMessage username={username} group={group!}/>,
                onOk: async () => {
                    try {
                        await Api.usersManagement.removeUserFromGroup(username, group!)
                        setLocalRefresh(localRefresh + 1);
                        message.success(`User removed from ${group} group!`);
                    } catch (e) {
                        message.error("Error while removing user from group");
                        throw (e)
                    }
                }
            },
            approve: {
                ...base,
                title: 'Approve user account',
                content: <ApproveMessage username={username}/>,
                onOk: async () => {
                    try {
                        await Api.usersManagement.confirmUser(username)
                        setLocalRefresh(localRefresh + 1);
                        // setUsers(users.filter((value: DataType) => username !== value.Username))
                        message.success("User approved!");
                    } catch (e) {
                        message.error("Error while approving user");
                        throw (e)
                    }
                }
            }
        }
        Modal.confirm(actions[action]);
    }

    return (
        <><ModalGroup username={user} setLocalRefresh={setLocalRefresh} isVisible={isVisible}
                      setVisible={setIsVisible}/>
            <Table dataSource={users} bordered loading={loading} scroll={{x: "max-content"}}>
                <ColumnGroup title="Name">
                    <Column title="First Name" dataIndex="Attributes" key="firstName"
                            render={(attribute: any) => {
                                if (attribute && find(attribute, 'name') && find(attribute, 'name').Value.split(' ')[0])
                                    return find(attribute, 'name').Value.split(' ')[0];
                                return '/';
                            }}/>
                    <Column title="Last Name" dataIndex="Attributes" key="lastName"
                            render={(attribute: any) => {
                                if (attribute && find(attribute, 'name') && find(attribute, 'name').Value.split(' ')[1])
                                    return find(attribute, 'name').Value.split(' ')[1];
                                return '/';
                            }}
                    />
                </ColumnGroup>
                <Column title="Email" dataIndex="Username" key="username"/>
                <Column title="Groups" dataIndex="Attributes" key="groups"
                        render={(attribute: any, {Username}: DataType) => (find(attribute, "groups").Value.map((group: string) =>
                            <span>
                            <Tag
                                closable={find(attribute, "groups").Value.length > 1}
                                onClose={(ev) => {
                                    ev.preventDefault();
                                    confirmAction(Username, "group", group)
                                }}
                                color={"green"}>{group}</Tag>
                        </span>))}/>
                <Column
                    title="User Status"
                    dataIndex="UserStatus"
                    key="status"
                    render={(status: string, index) => (
                        <div key={`status-${index}`}>
                            <Tag
                                color={status === 'CONFIRMED' ? 'green' : 'orange'}>{status}</Tag>
                        </div>
                    )}
                />
                <Column
                    title="Action"
                    key="action"
                    width={"20%"}
                    render={(_, {Username, UserStatus, AuthEvents}: DataType, index) => (
                        <Space key={`space-${index}`} size="middle">
                            <Button type="link" onClick={() => {
                                Modal.info({
                                    title: 'User login history',
                                    content: <div style={{maxHeight: 500, overflow: "auto"}}><Tree
                                        defaultExpandAll
                                        showIcon
                                        treeData={AuthEvents.map((event, key) => ({
                                            title: DateTime.fromSQL(event.CreationDate).toFormat("yyyy LLL dd - tttt"),
                                            key: `${event.CreationDate}-${key}`,
                                            icon: <ClockCircleTwoTone />,
                                            children: [
                                                {
                                                    title: `Event type: ${event.EventType}`,
                                                    key: `${event.EventType}-0-${key}`,
                                                    icon: <InfoCircleTwoTone />
                                                },
                                                {
                                                    title: `Response: ${event.EventResponse}`,
                                                    key: `${event.EventResponse}-1-${key}`,
                                                    icon: event.EventResponse.toLowerCase() === "fail" ? <InfoCircleTwoTone twoToneColor="#eb2f96"/> : <InfoCircleTwoTone />
                                                },
                                            ],
                                        }))}
                                    />
                                    </div>,
                                });
                            }}>Activity</Button>
                            {type === Type.UNCONFIRMED ?
                                <Button type="link"
                                        disabled={UserStatus === Type.FORCE_CHANGE_PASSWORD}
                                        onClick={() => confirmAction(Username, "approve")}>Approve</Button> :
                                <Button type="link" onClick={() => {
                                    setUser(Username)
                                    setIsVisible(!isVisible)
                                }}>Add to group</Button>}
                            <Button disabled={StorageHelper.getUser().email === Username} type="link" onClick={() => {
                                confirmAction(Username, "delete")
                            }}>Delete</Button>
                        </Space>
                    )}
                />
            </Table>
        </>
    )
}

export default UserList;
